import { Component, Input, Output, EventEmitter, ElementRef, AfterViewInit, ContentChildren, QueryList } from '@angular/core';
import { ResizableGridColumnComponent } from '../resizable-grid-column/resizable-grid-column.component';

@Component({
    selector: 'app-resizable-grid-row',
    template: `
    <div class="resizable-grid-row" [style.top.%]="top" [style.height.%]="height">
      <ng-content></ng-content>
      @if (!isLast) {
        <div class="resizer bottom" (mousedown)="onResizeStart($event)"></div>
      }
    </div>
    `,
    styles: [`
    .resizable-grid-row {
      position: absolute;
      left: 0;
      right: 0;
    }
    .resizer {
      position: absolute;
      left: 0;
      right: 0;
      height: 5px;
      bottom: 0;
      background-color: #ccc;
      cursor: row-resize;
    }
  `],
    standalone: false
})
export class ResizableGridRowComponent implements AfterViewInit {
  @Input() top: number = 0;
  @Input() height: number = 50;
  @Input() minHeight: number = 5;
  @Input() isLast: boolean = false;
  @Output() resized = new EventEmitter<{index: number, newHeight: number}>();
  @ContentChildren(ResizableGridColumnComponent) columns!: QueryList<ResizableGridColumnComponent>;
  index: number = 0;

  constructor(private el: ElementRef) {}

  ngAfterViewInit() {
    this.updatePosition();
    this.initializeColumns();
  }

  updatePosition() {
    const element = this.el.nativeElement;
    element.style.top = `${this.top}%`;
    element.style.height = `${this.height}%`;
  }

  initializeColumns() {
    this.columns.forEach((column, index) => {
      column.index = index;
      column.resized.subscribe((data: {index: number, newWidth: number}) => this.onColumnResized(data));
    });
  }

  onColumnResized(data: {index: number, newWidth: number}) {
    const { index, newWidth } = data;
    const columnArray = this.columns.toArray();
    const currentColumn = columnArray[index];
    const nextColumn = columnArray[index + 1];

    if (!nextColumn) return; // Can't resize the last column

    const currentWidth = currentColumn.width;
    const nextWidth = nextColumn.width;
    const totalWidth = currentWidth + nextWidth;

    const diff = newWidth - currentWidth;

    // Check if the next column would go below its minimum width
    if (nextWidth - diff < nextColumn.minWidth) {
      // Adjust the new width to respect the minimum width of the next column
      const maxChange = nextWidth - nextColumn.minWidth;
      currentColumn.width = currentWidth + maxChange;
      nextColumn.width = nextColumn.minWidth;
    } else {
      currentColumn.width = newWidth;
      nextColumn.width = totalWidth - newWidth;
    }

    currentColumn.updatePosition();
    nextColumn.left = currentColumn.left + currentColumn.width;
    nextColumn.updatePosition();

    // Update all subsequent columns' positions
    for (let i = index + 2; i < columnArray.length; i++) {
      columnArray[i].left = columnArray[i - 1].left + columnArray[i - 1].width;
      columnArray[i].updatePosition();
    }
  }

  onResizeStart(event: MouseEvent) {
    event.preventDefault();
    const startY = event.clientY;
    const startHeight = this.height;
    const parentHeight = this.el.nativeElement.parentElement.offsetHeight;

    const onMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - startY;
      const deltaPercent = (deltaY / parentHeight) * 100;
      const newHeight = Math.max(this.minHeight, Math.min(100 - this.top, startHeight + deltaPercent));
      this.resized.emit({index: this.index, newHeight});
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }
}
